import { useState, Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { activeSectionWithChildren, navTitle } from '../../lib/nav-items'
import { Sidebar } from './Sidebar'
import { MobileDrawer } from './MobileDrawer'
import { MobileBottomNav } from './MobileBottomNav'
import { PageSkeleton } from './PageSkeleton'
import { SectionTabs } from './SectionTabs'

export default function Layout({ children }: { children: React.ReactNode }) {
	const [isDrawerOpen, setDrawerOpen] = useState(false)
	const { pathname } = useLocation()
	const { hasPermission } = useAuth()

	return (
		<div className="flex min-h-screen">
			<aside className="hidden md:flex print:hidden sticky top-0 h-screen w-52 bg-chrome p-4 flex-col shrink-0">
				<img src="/logo_ricepaper.svg" alt="SAYSAY" className="mb-6 size-14" />
				<Sidebar />
			</aside>

			{/* Trap: min-w-0 keeps this column from growing to its widest child. Without it
			     the overflow-x-hidden below never gets the chance to clip anything. */}
			<div className="relative flex-1 min-w-0 flex flex-col min-h-screen">
				<header className="md:hidden print:hidden flex flex-col px-4 pt-safe pb-3 bg-chrome sticky top-0 z-30">
					<span className="text-chrome-foreground text-sm font-medium tracking-wider">
						{activeSectionWithChildren(pathname, hasPermission)?.label ?? navTitle(pathname, hasPermission)}
					</span>
					<SectionTabs />
				</header>

				{/* Trap: No key and no enter animation here. Remounting the content area on
				    every path change re-ran the fade on top of the real problem, which was
				    nav entries pointing at redirect routes that render without this
				    layout for a frame. See the note in nav-items.ts. */}
				<main className="flex-1 bg-background pt-4 px-4 pb-[calc(1rem+3.5rem+env(safe-area-inset-bottom))] md:p-7 overflow-y-auto overflow-x-hidden">
					<Suspense fallback={<PageSkeleton />}>{children}</Suspense>
				</main>
			</div>

			<MobileDrawer isOpen={isDrawerOpen} onClose={() => setDrawerOpen(false)} />
			<MobileBottomNav onMoreClick={() => setDrawerOpen(true)} />
		</div>
	)
}
